import isNil from 'lodash/isNil' // only here for the unit test that requires TSX
import isObject from 'lodash/isObject'
import { EasySearchHitDto, EasySearchOverviewDto } from '@dhl/mdp-api'
import { ErrorIcon } from '~/common/components/icons/ErrorIcon'
import { WarningYellowIcon } from '~/common/components/icons'

export const filterNullsDeeply = (object: Record<string, any>): Record<string, any> =>
  Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      ...(isNil(value) ? {} : { [key]: isObject(value) ? filterNullsDeeply(value) : value }),
    }),
    {}
  )

/*
 * I realize that these methods are not fail-safe at all, but they are good enough for the purpose of this exercise:
 * rendering PPP data. They work for all the examples I have seen so far.
 * I reflected the uncertainty in the method names.
 */
export const estimateLines = (str: string): number => str.split('\n').length

export const isProbablyJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

export const isProbablyCsv = (str: string, csvSeparator = ';'): boolean => {
  const lines = str.split('\n')
  if (lines.length < 2) return false
  const first = lines[0].split(csvSeparator)
  const second = lines[1].split(csvSeparator)
  return first.length === second.length && first.length > 1
}

export const isProbablyXml = (str: string): boolean => str.startsWith('<') && str.endsWith('>')

export const hitHasWarningOrError = (hit: EasySearchHitDto): boolean => Boolean(hit.errors && hit.errors.length > 0)

// This function expects errors[] to be present so it is not safe to use it without checking first
export const activeErrorLevelForHit = (hit: EasySearchHitDto): string =>
  hit.errors!.find(({ errorLevel }) => errorLevel === 'Error') ? 'Error' : 'Warning'

// This function expects errors[] to be present so it is not safe to use it without checking first
export const maybeHitWarningOrErrorIcon = (hit: EasySearchHitDto) =>
  activeErrorLevelForHit(hit) === 'Error' ? <ErrorIcon /> : <WarningYellowIcon />

export const maybeOverviewWarningOrErrorIcon = (hits: EasySearchHitDto[]) =>
  function ({ correlationId: overviewCorrelationId }: EasySearchOverviewDto) {
    return hits
      .filter(({ correlationId: hitCorrelationId }) => hitCorrelationId === overviewCorrelationId)
      .some((hit) => activeErrorLevelForHit(hit) === 'Error') ? (
      <ErrorIcon />
    ) : (
      <WarningYellowIcon />
    )
  }

export const overviewHasErrorGenerator =
  (hits: EasySearchHitDto[]) =>
  ({ correlationId }: EasySearchOverviewDto): boolean =>
    hits.filter(({ correlationId: hitCorrelationId }) => hitCorrelationId === correlationId).some(hitHasWarningOrError)

export const overviewSomeHaveFilenames = (overview: EasySearchOverviewDto[]): boolean =>
  overview.some(({ fileName }) => Boolean(fileName))

export const sanitizeParcelId = (parcelId: string): string =>
  parcelId
    // remove all non-alphanumeric characters except for hyphens and underscores:
    .replace(/[^a-zA-Z0-9-_]/g, '')
    // remove strange characters that might have been introduced by copy-pasting:
    .replace(/<[^>]*>/g, '')
    .trim()

/**
 * Recursively searches for all occurences of a property with the specified name in a nested object and returns their values.
 * @param obj The object to search within
 * @param name The property name to find
 * @returns An array of values for the specified property name
 */
export const findPropertyRecursively = (obj: unknown, name: string) => {
  const results: string[] = []

  const recurse = (value: unknown) => {
    if (value === null || typeof value !== 'object') {
      return
    }

    if (Array.isArray(value)) {
      value.forEach(recurse)
    } else {
      for (const [key, val] of Object.entries(value)) {
        if (key === name && value) {
          results.push(val)
        }

        recurse(val)
      }
    }
  }

  recurse(obj)

  return results
}
